import { useId, type ReactNode, type TextareaHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';
import { Select, type SelectProps } from '@/components/ui/select';

const CONTROL_BASE =
  'w-full rounded-control border bg-white text-sm text-ink-800 transition-all duration-200 ' +
  'focus:ring-4 focus:outline-none disabled:cursor-not-allowed disabled:bg-ink-50 disabled:opacity-70';

const CONTROL_OK = 'border-ink-200 focus:border-brand-400 focus:ring-brand-100';
const CONTROL_ERROR = 'border-rose-300 focus:border-rose-400 focus:ring-rose-100';

interface BaseProps {
  label: string;
  hint?: string;
  error?: string;
}

function Etiqueta({ id, label, hint, error }: BaseProps & { id: string }) {
  return (
    <>
      <label htmlFor={id} className="text-sm font-medium text-ink-700">
        {label}
      </label>
      {error ? (
        <p id={`${id}-error`} className="text-xs text-rose-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-ink-400">
          {hint}
        </p>
      ) : null}
    </>
  );
}

export interface ControlSelectProps extends BaseProps, SelectProps {
  children: ReactNode;
  className?: string;
  containerClassName?: string;
}

/** Select con label, ayuda y error (mismo aspecto que `Input`). */
export function ControlSelect({
  label,
  hint,
  error,
  children,
  className,
  containerClassName,
  id,
  ...rest
}: ControlSelectProps) {
  const autoId = useId();
  const selectId = id ?? `select-${autoId}`;
  const describedBy = error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      <Etiqueta id={selectId} label={label} hint={hint} error={error} />

      <Select
        id={selectId}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL_BASE, error ? CONTROL_ERROR : CONTROL_OK, 'h-10 px-2.5', className)}
        {...rest}
      >
        {children}
      </Select>
    </div>
  );
}

export interface ControlAreaProps
  extends BaseProps,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  className?: string;
  containerClassName?: string;
}

/** Área de texto con label, ayuda y error. */
export function ControlArea({
  label,
  hint,
  error,
  className,
  containerClassName,
  id,
  rows = 3,
  ...rest
}: ControlAreaProps) {
  const autoId = useId();
  const areaId = id ?? `area-${autoId}`;
  const describedBy = error ? `${areaId}-error` : hint ? `${areaId}-hint` : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      <Etiqueta id={areaId} label={label} hint={hint} error={error} />

      <textarea
        id={areaId}
        rows={rows}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        className={cn(
          CONTROL_BASE,
          error ? CONTROL_ERROR : CONTROL_OK,
          'resize-y px-3 py-2 leading-relaxed',
          className,
        )}
        {...rest}
      />
    </div>
  );
}
